'use client'

import { useEffect, useState } from 'react'
import Drawer from '@/components/ui/Drawer'
import ProjectForm from './ProjectForm'
import { Project } from '@/lib/types'

interface EditProjectDrawerProps {
  isOpen: boolean
  onClose: () => void
  project: Project | null
  onProjectUpdated: (projectId: string, updates: Partial<Omit<Project, 'id' | 'createdAt' | 'userId'>>) => Promise<void>
}

export default function EditProjectDrawer({ 
  isOpen, 
  onClose, 
  project, 
  onProjectUpdated 
}: EditProjectDrawerProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async (projectData: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'userId'>) => {
    if (!project) return

    try {
      setIsSubmitting(true)
      await onProjectUpdated(project.id, projectData)
      onClose()
    } catch (error) {
      console.error('Failed to update project:', error)
      // Error is handled by the parent context
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose()
    }
  }

  // Reset form when project changes
  useEffect(() => {
    if (isOpen && project) {
      // Form will automatically populate with project data
    }
  }, [isOpen, project])

  if (!project) return null

  return (
    <Drawer isOpen={isOpen} onClose={handleCancel} title="Edit Project">
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Edit Project
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Update your project details and priorities
          </p>
        </div>

        <ProjectForm
          project={project}
          onSave={handleSave}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    </Drawer>
  )
}
